import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { PlusCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CompanySelector } from "../compare/CompanySelector";
import { addHolding } from "../../lib/api";
import { formatINR } from "../../lib/format";

// Premium, search-driven add-holding experience — reuses the Compare page's
// CompanySelector for consistent quality. Quantity is added at the latest
// verified price already returned by /companies (no custom cost basis yet).
export const AddHoldingPanel = ({ universe, onAdded, onClose, testId = "portfolio-add-holding" }) => {
  const [ticker, setTicker] = React.useState("");
  const [quantity, setQuantity] = React.useState(1);
  const [submitting, setSubmitting] = React.useState(false);
  const [selectorOpen, setSelectorOpen] = React.useState(false);
  const [error, setError] = React.useState("");
  const selected = universe.find((c) => c.ticker === ticker);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !selectorOpen) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, selectorOpen]);

  const submit = async (event) => {
    event?.preventDefault();
    if (!selected) {
      setError("Choose a company before adding a holding.");
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      setError("Enter a quantity greater than zero.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const next = await addHolding({ ticker: selected.ticker, quantity: Number(quantity), avg_price: selected.price });
      onAdded(next);
      toast.success(`Added ${quantity} × ${selected.ticker} to your research portfolio`);
      onClose();
    } catch (e) {
      setError("The holding could not be added. Please try again.");
      toast.error("Couldn't add holding — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, height: 0 }}
      animate={{ opacity: 1, y: 0, height: "auto" }}
      exit={{ opacity: 0, y: -8, height: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="overflow-hidden"
    >
      <form className="lv-panel lv-top-edge p-4 sm:p-5" onSubmit={submit} noValidate>
        <div className="mb-3 flex items-center justify-between">
          <span className="kicker">New Holding</span>
          <button type="button" data-testid={`${testId}-cancel`} onClick={onClose} className="font-mono text-[11px] text-zinc-500 transition-colors hover:text-zinc-300">Cancel</button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
          <CompanySelector testId={`${testId}-company`} label="Company" value={ticker} onChange={setTicker} universe={universe} accent="amber" onOpenChange={setSelectorOpen} />
          <div>
            <label htmlFor={`${testId}-quantity-input`} className="kicker mb-1.5 block text-amber-500/70">Quantity</label>
            <input
              id={`${testId}-quantity-input`}
              type="number"
              min={1}
              required
              value={quantity}
              data-testid={`${testId}-quantity`}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full rounded-lg border border-white/[0.12] bg-[#0B0C0E] px-3 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-amber-500/40"
            />
          </div>
          <button
            type="submit"
            data-testid={`${testId}-submit`}
            disabled={submitting}
            className="lv-press inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 font-mono text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />}
            Add
          </button>
        </div>
        {error && <p role="alert" className="mt-2.5 text-xs text-rose-300">{error}</p>}
        {selected && (
          <p className="mt-2.5 font-mono text-[11px] text-zinc-500">
            Added at the latest verified price of {formatINR(selected.price)} per share — custom cost basis isn't supported by the simulator yet.
          </p>
        )}
      </form>
    </motion.div>
  );
};
